import { useReduxState } from 'hooks';
import { Tooltip } from 'components/common';
import { Icon } from 'components/common/icons';
import { CompetencyMatrixMatchStatus } from '.';

import type { GetUsersForCompetencyMatrixRes } from 'shared-types/responses';
import type { CompetencyInMatrix } from 'lib/competencies/getCompetencyMatrixColumnsByView';
import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  competency: CompetencyInMatrix;
  user: GetUsersForCompetencyMatrixRes['users'][0];
  viewLevelsSeparately?: boolean;
}

const CompetencyMatrixUsersListCompetencyMatch = ({
  competency,
  user,
  viewLevelsSeparately
}: Props) => {
  const { theme } = useReduxState();

  const numberOfLevels = competency.levels?.length || 0;

  const iconStyles: Record<string, { icon: IconProp; color: string }> = {
    true: { icon: 'check-circle', color: theme.successColor },
    false: { icon: 'times-circle', color: theme.lightGreyDarkActive }
  };

  return (
    <div>
      <Tooltip
        content={
          <div className="tooltip">
            {viewLevelsSeparately && numberOfLevels > 0 ? (
              <div className="title">{competency.title}</div>
            ) : null}
            {viewLevelsSeparately && numberOfLevels > 0 ? (
              competency.levels?.map((level, i) => (
                <div className="level" key={level.value}>
                  <Icon
                    {...iconStyles[
                      `${
                        Number(user.competencies?.[competency.value]) >= i + 1
                      }`
                    ]}
                    style={{ position: 'relative', top: 2 }}
                  />
                  <span className="level-label">{level.label}</span>
                </div>
              ))
            ) : (
              <div className="level">
                <Icon
                  {...iconStyles[
                    `${Boolean(
                      (competency.level &&
                        (user.competencies?.[competency.value] as number) >=
                          competency.level) ||
                        (!competency.level &&
                          user.competencies?.[competency.value])
                    )}`
                  ]}
                  style={{ position: 'relative', top: 2 }}
                />
                <span className="title">
                  {competency.title}
                  {competency.levelTitle ? (
                    <>
                      <span>{': '}</span>
                      <span className="title-level">
                        {competency.levelTitle}
                      </span>
                    </>
                  ) : null}
                </span>
              </div>
            )}
          </div>
        }
        isLight={true}
      >
        <CompetencyMatrixMatchStatus
          progress={
            numberOfLevels > 0
              ? user.competencies?.[competency.value]
                ? Number(user.competencies?.[competency.value]) / numberOfLevels
                : 0
              : (competency.level &&
                  (user.competencies?.[competency.value] as number) >=
                    competency.level) ||
                (!competency.level && user.competencies?.[competency.value])
              ? 1
              : 0
          }
        />
      </Tooltip>

      <style jsx>{`
        .tooltip {
          align-items: start;
          display: flex;
          flex-direction: column;
          gap: 6px;
          justify-content: flex-start;
          min-width: 100px;
          padding: 4px 2px;
          width: 100%;
        }
        .title {
          color: ${theme.textColorLight};
          text-align: left;
        }
        .title-level {
          font-weight: 400;
        }
        .level {
          display: flex;
          gap: 7px;
        }
        .level-label {
          color: ${theme.textColorLighter};
          text-align: left;
        }
      `}</style>
    </div>
  );
};

export default CompetencyMatrixUsersListCompetencyMatch;
